import { z } from 'zod';

export const PrinterDriverSchema = z.enum([
  'brother_ql',
  'zpl',
  'escpos',
  'tspl',
  'cpcl',
  'niimbot',  // T-082: driver Niimbot (B1/B21, SPP binario) - in fase di reverse-engineering
  'preview',
  'system',   // T-272: stampa via driver di sistema (PWA Chrome desktop → window.print, es. Meteor Mirò USB)
  'qz',       // T-273: stampa via QZ Tray (helper locale) → stampante di sistema scelta PER NOME, silenziosa
]);
export type PrinterDriver = z.infer<typeof PrinterDriverSchema>;

// T-082: aggiunto 'ble' per stampanti Bluetooth Low Energy native
// (Niimbot B1/B21/D11, Phomemo, alcune Brother). 'bluetooth' resta per
// BT Classic SPP (Clabel CT221B, Xprinter, HPRT).
export const PrinterConnectionSchema = z.enum(['bluetooth', 'ble', 'wifi', 'usb']);
export type PrinterConnection = z.infer<typeof PrinterConnectionSchema>;

export const PrinterSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid(),
  name: z.string().min(1).max(200),
  driver: PrinterDriverSchema,
  connection: PrinterConnectionSchema,
  address: z.string().max(200),
  label_size_mm: z.object({ width: z.number().positive(), height: z.number().positive() }),
  dpi: z.number().int().positive().default(203),
  default_template_id: z.string().uuid().nullable(),
  active: z.boolean().default(true),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
  version: z.number().int().nonnegative().default(0),
});
export type Printer = z.infer<typeof PrinterSchema>;

export const LabelPresetSchema = z.enum([
  'takeaway',
  'decongelato',
  'lavorato',
  'ingrediente_aperto',
  'ricetta',
  'tracciabilita_lotto',
]);
export type LabelPreset = z.infer<typeof LabelPresetSchema>;

export const LABEL_PRESET_LABELS: Record<LabelPreset, string> = {
  takeaway: 'Asporto',
  decongelato: 'Decongelato',
  lavorato: 'Lavorato',
  ingrediente_aperto: 'Ingrediente aperto',
  ricetta: 'Ricetta / Preparazione',
  tracciabilita_lotto: 'Tracciabilità lotto',
};

export const LabelElementSchema = z.object({
  id: z.string(),
  type: z.enum(['text', 'barcode', 'qr', 'image', 'rect', 'line', 'expiry_weekday']),
  x_mm: z.number(),
  y_mm: z.number(),
  width_mm: z.number().positive().nullable().optional(),
  height_mm: z.number().positive().nullable().optional(),
  font_size_pt: z.number().positive().nullable().optional(),
  font_weight: z.enum(['normal', 'bold']).default('normal').optional(),
  // Allineamento orizzontale del testo dentro `width_mm`. Default 'left' per
  // retro-compatibilità. Usato dal renderer canvas (labelRaster) — sui driver
  // che non leggono il canvas finale (es. ZPL nativo) viene ignorato.
  align: z.enum(['left', 'center', 'right']).default('left').optional(),
  text_fit: z.enum(['wrap', 'shrink']).optional(),
  qr_format: z.enum(['qr', 'rmqr']).optional(),
  binding: z.string().nullable().optional(),
  static_value: z.string().nullable().optional(),
  rotation: z.union([z.literal(0), z.literal(90), z.literal(180), z.literal(270)]).default(0),
});
export type LabelElement = z.infer<typeof LabelElementSchema>;

export const LabelTemplateSchema = z.object({
  id: z.string().uuid(),
  tenant_id: z.string().uuid(),
  business_id: z.string().uuid().nullable(),
  name: z.string().min(1).max(200),
  preset: LabelPresetSchema,
  width_mm: z.number().positive(),
  height_mm: z.number().positive(),
  elements: z.array(LabelElementSchema),
  required_allergens: z.boolean().default(false),
  created_at: z.string().datetime(),
  archived_at: z.string().datetime().nullable(),
  version: z.number().int().nonnegative().default(0),
});
export type LabelTemplate = z.infer<typeof LabelTemplateSchema>;

// T-303 (collettiva, pilota Bluecoop) — lista alimenti per la PRE-STAMPA in serie.
// È una lista di CONFIGURAZIONE (persistita in
// `tenants.settings.collettiva_prelabel_list`, non una tabella): il titolare la
// personalizza una volta (seed dal catalogo ordini del preposto, poi add/remove
// righe) e la riusa ogni giorno. Ogni riga ha un numero di copie SUGGERITO
// fisso, ritoccabile al momento della stampa.
export const CollettivaPrelabelRowSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(200),
  // Copie suggerite di default per la riga (0..999). 0 = riga presente ma non
  // pre-selezionata per la stampa. NB: sono ETICHETTE di margine, NON le
  // porzioni prodotte (che arrivano dagli ordini del mattino).
  suggested_copies: z.number().int().min(0).max(999).default(0),
  sort_order: z.number().int().default(0),
  // T-380: ricetta agganciata alla riga (nuovo modello recipe-based). Se
  // presente, la pre-stampa crea una `productions` con questo `recipe_id`
  // (prestampa=true) che il piano del mattino RIUSA senza doppioni. Null =
  // riga "ordinario" (recipe risolto dalla rotazione del giorno) o riga
  // legacy solo-nome (retrocompatibile). `ordinario_cat` marca le righe
  // ordinarie da risolvere dalla rotazione (PRIMI/SECONDI/CONTORNI).
  recipe_id: z.string().uuid().nullable().optional(),
  ordinario_cat: z.enum(['PRIMI', 'SECONDI', 'CONTORNI']).nullable().optional(),
  // T-406 (nota Annalisa A4): categoria della riga per raggruppare la lista
  // pre-stampa per tipologia (Primi/Secondi/…), come l'ordine preposto.
  category: z.enum(['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA', 'ALTRO']).nullable().optional(),
  // T-406 (nota Annalisa A3): riga "morbido" — stessa ricetta dell'ordinario del
  // giorno della sua categoria (ordinario_cat), ma il nome stampato ha il
  // suffisso "morbido". Usare insieme a ordinario_cat. (deprecato: usare variant_suffix)
  morbido: z.boolean().nullable().optional(),
  // T-406 FIX (diete): riga = stessa ricetta dell'ordinario del giorno (ordinario_cat)
  // ma il nome stampato aggiunge questo SUFFISSO (es. "diabetico", "iposodico",
  // "no latticini", "morbido"). Es. "1° Ordinario diabetico" → in stampa
  // "<primo del giorno> diabetico". Va usato con ordinario_cat.
  variant_suffix: z.string().max(40).nullable().optional(),
});
export type CollettivaPrelabelRow = z.infer<typeof CollettivaPrelabelRowSchema>;

export const CollettivaPrelabelListSchema = z.array(CollettivaPrelabelRowSchema);
export type CollettivaPrelabelList = z.infer<typeof CollettivaPrelabelListSchema>;

// T-347 (collettiva, pilota Bluecoop) — BOZZA di SCARICO MERCE della giornata.
// Lo chef prepara le righe (lotto FEFO + qtà) e SALVA (draft, non scarica); il
// responsabile (owner/consulente/ristoratore) rilegge, corregge e SCARICA &
// CHIUDE (emette gli stock_out + closed=true). Persistita in
// `tenants.settings.collettiva_discharge` come mappa `{ "<biz>_<data>_<meal>":
// draft }` (nessuna tabella nuova; sincronizzata via master `tenants`). Righe
// custom = articoli aggiunti a mano (mancanti) o split su un altro lotto quando
// il primo si esaurisce.
export const CollettivaDischargeLineSchema = z.object({
  food_id: z.string().min(1),
  food_name: z.string().default(''),
  batch_lot: z.string().nullable().default(null),
  expiration_date: z.string().nullable().default(null),
  qty: z.number().min(0).default(0),
  uom: z.string().default('pz'),
  // riga aggiunta a mano (non derivata dal fabbisogno del piano).
  custom: z.boolean().default(false),
});
export type CollettivaDischargeLine = z.infer<typeof CollettivaDischargeLineSchema>;

export const CollettivaDischargeDraftSchema = z.object({
  lines: z.array(CollettivaDischargeLineSchema).default([]),
  closed: z.boolean().default(false),
  // Durable lineage: after this field is set, append-only StockCommand events
  // (not the cache flag above) determine whether the sheet is closed. It also
  // survives a restart when a terminal rejection has removed optimistic events.
  stock_command_id: z.string().uuid().nullable().default(null),
  saved_by: z.string().nullable().default(null),
  saved_at: z.string().nullable().default(null),
  closed_by: z.string().nullable().default(null),
  closed_at: z.string().nullable().default(null),
});
export type CollettivaDischargeDraft = z.infer<typeof CollettivaDischargeDraftSchema>;

// Mappa keyed per (business, data, servizio) → bozza. Chiave: `${biz}_${date}_${meal}`.
export const CollettivaDischargeMapSchema = z.record(z.string(), CollettivaDischargeDraftSchema);
export type CollettivaDischargeMap = z.infer<typeof CollettivaDischargeMapSchema>;
